import iconWindowsDevice from "assets/IconWindowsDevice.svg?url";

import NuSingleSelect from "js/components/NuSingleSelect";
import Checkbox from "js/components/shared/Checkbox";
import RuleStatusToggle from "js/components/shared/RuleStatusToggle";
import StandardModal from "js/components/shared/StandardModal";
import DisableKeyboardNavigation from "js/components/utils/DisableKeyboardNavigation";
import useStore from "stores/index";
import { useSelectedClassroomStore } from "stores/v2/selectedClassroomStore";
import { getUserDisplayTextForSelect, userDisplayText } from "js/utils/userUtils";
import _ from "lodash";
import PropTypes from "prop-types";
import React from "react";
import { Trans, withTranslation } from "react-i18next";
import SafeImage from "../SafeImage";
import { DangerButton } from "../shared/button/DangerButton";
import { GreyedButton } from "../shared/button/GreyedButton";
import { PrimaryButton } from "../shared/button/PrimaryButton";
import { RuleStatusPill } from "../shared/RuleStatusPill";
import { useClassroomPolicyStore } from "stores/v2/classroomPolicyStore";
import { Spinner } from "@chakra-ui/react";
import { ChakraFAIcon } from "@/components/ui/Icon";
import { WarningBanner } from "@/components/WarningBanner";
import { useClasswizeConfigStore } from "@/stores/classwizeConfigStore";
import { convertWebsiteToSignature } from "@/utils/convertWebsiteToSignature";
import { useSignatureStore } from "@/stores/v2/signatureStore";

const SIGNATURE = "SIGNATURE";
const APP_CONTROL = "APP_CONTROL";

class CreatePolicy extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			id: undefined,
			signature: undefined,
			user: undefined,
			originalUser: undefined,
			editedlUser: undefined,
			type: "blocked",
			originalType: "blocked",
			signatureValueInvalid: false,
			signatureError: "",
			userValueInvalid: false,
			userError: "",
			website: undefined,
			wholeClass: true,
			modifiedWebsite: false,
			classwizeConfig: useClasswizeConfigStore.getState().data,
			saving: false,
			duplicateOrConflictRule: undefined,
			validatedPolicy: undefined,
			resolving: false,
			validationErrorCode: undefined,
			signatureType: null,
			isBrowser: false,
			appControlValidationError: false,
			networkError: false,
			enabled: true,
		};

		this.formRef = React.createRef();
		this.featureFlags = useStore.getState().featureToggles;
	}

	componentDidUpdate(prevProps) {
		// React to validation response (e.g. 409) so modal can show warning even when policy didn't change
		if (!_.isEqual(this.props.validationResponseData, prevProps.validationResponseData)) {
			if (this.props.validationResponseData?.networkError) {
				this.handleOnApiError();
			} else {
				this.handleValidationResponse(this.props.validationResponseData, this.props.policy);
			}
		}

		if (_.isEmpty(this.props.nativeAppControl) && !_.isEqual(this.props.policy, prevProps.policy)) {
			const policyType = this.props.policy.type === undefined ? "blocked" : this.props.policy.type;
			// _sourceType: which list the rule lives in (e.g. when opening from drawer after 409, rule is still in blocked list)
			const sourceType = this.props.policy._sourceType ?? policyType;
			this.setState({
				signatureType: SIGNATURE,
				id: this.props.policy.id,
				signature: this.props.policy.tag,
				website: this.props.policy.website,
				user: this.props.policy.user,
				originalUser: this.props.policy.user,
				type: policyType,
				originalType: sourceType,
				wholeClass: !this.props.policy.user,
				modifiedWebsite: false,
				enabled: this.props.policy.enabled ?? true,
			});

			if (
				!_.isEqual(this.props.validationResponseData, prevProps.validationResponseData) &&
				this.props.validationResponseData.networkError
			) {
				this.handleOnApiError();
			} else if (!_.isEqual(this.props.validationResponseData, prevProps.validationResponseData)) {
				this.handleValidationResponse(this.props.validationResponseData, this.props.policy);
			}
		} else if (!_.isEmpty(this.props.nativeAppControl) && !_.isEqual(this.props.nativeAppControl, prevProps.nativeAppControl)) {
			const user = useSelectedClassroomStore
				.getState()
				.students?.find((student) => student.stableIdV1 === this.props.nativeAppControl.studentStableIdV1)?.username;
			this.setState({
				signatureType: APP_CONTROL,
				signature: this.props.nativeAppControl.appControlId,
				user: user,
				originalUser: user,
				wholeClass: _.isEmpty(this.props.nativeAppControl.studentStableIdV1),
				type: this.props.nativeAppControl.action === "block" ? "blocked" : "allowed",
			});
		}
	}

	prepareSelectorItems = () => {
		const ret = [];
		const { t } = this.props;
		const { signatures, findSignature } = useSignatureStore.getState();

		const availableNativeAppControls = this.props.nativeAppControlsDb.nativeAppControls;
		availableNativeAppControls.sort(function (a, b) {
			return a.name.localeCompare(b.name);
		});

		ret.push({ type: "section", text: t("policyDrawer.sectionSignaturesAndCategories") });
		for (const signature of signatures) {
			if (
				signature.enabled &&
				signature.id !== "sphirewall.application.porn" &&
				signature.id !== "sphirewall.application.childporn" &&
				signature.id !== "sphirewall.application.offensive"
			) {
				ret.push({ value: signature.id, text: signature.name });
			}
		}

		if (this.featureFlags.isNativeAppControlsEnabled()) {
			ret.push({ type: "section", text: t("policyDrawer.sectionAppControls") });
			for (const appControl of availableNativeAppControls) {
				let icons = [];
				if (appControl.platforms.includes("windows")) {
					icons.push(iconWindowsDevice);
				}
				ret.push({ value: appControl.id, text: appControl.name, icons });
			}
		}

		ret.push({
			shouldShow: function (value) {
				return this.isOk(value);
			},
			renderText: function (value) {
				// If the entered URL maps to a known signature, show that signature's name instead of the raw website.
				const matchedSignatureId = convertWebsiteToSignature(value);
				const matchedSignature = matchedSignatureId ? findSignature(matchedSignatureId) : undefined;
				if (matchedSignature) {
					return matchedSignature.name;
				}
				return t("createPolicy.websiteOptionPrefix", { value });
			},
			resolveDynamic: function (value) {
				// When the URL maps to a known signature, group it under the signatures section and use the signature's value.
				const matchedSignatureId = convertWebsiteToSignature(value);
				const matchedSignature = matchedSignatureId ? findSignature(matchedSignatureId) : undefined;
				if (matchedSignature) {
					return { value: matchedSignatureId, section: t("policyDrawer.sectionSignaturesAndCategories") };
				}
				return null;
			},
			isOk: function (value) {
				return this.transformFilter(value) !== "";
			},
			transformFilter: function (value) {
				const re = /^(http:\/\/|https:\/\/|)([a-z0-9A-Z.-]+\.?)\.([a-z0-9A-Z.-]+)(\/|)(.*)/i;
				let url = "";
				if (re.test(value)) {
					const m = re.exec(value);
					if (m[2] !== "") {
						url += m[2];
					}
					if (m[3] !== "") {
						url += "." + m[3];
					}
					if (m[4] !== "") {
						url += m[4];
					}
					if (m[5] !== "") {
						url += m[5];
					}
				}
				return url;
			},
			dynamic: true,
			value: "website",
		});

		// need to add this, otherwise when editing a rule set on a website, nothing shows in NuSingleSelect
		if (this.props.policy.website) {
			ret.push({
				value: this.props.policy.website,
				text: this.props.policy.website,
			});
		}

		return ret;
	};

	prepareAvailableUsers = () => {
		const ret = [];
		this.props.users.sort(function (a, b) {
			return a.username.localeCompare(b.username);
		});

		for (const user of this.props.users) {
			ret.push({
				text: getUserDisplayTextForSelect(user),
				value: user.username,
			});
		}

		return ret;
	};

	changeSignature = (value, filter) => {
		if (value === "website") {
			// If the pasted/typed URL maps to a known signature, use that signature instead of a raw website rule.
			const matchedSignatureId = convertWebsiteToSignature(filter);
			const matchedSignature = matchedSignatureId ? useSignatureStore.getState().findSignature(matchedSignatureId) : undefined;
			if (matchedSignature) {
				this.setState({
					signatureType: SIGNATURE,
					signature: matchedSignatureId,
					website: undefined,
					signatureValueInvalid: false,
					signatureError: "",
					modifiedWebsite: true,
					isBrowser: matchedSignature.isBrowser,
				});
				return;
			}

			this.setState({
				signatureType: SIGNATURE,
				signature: undefined,
				website: filter,
				signatureValueInvalid: false,
				signatureError: "",
				modifiedWebsite: true,
			});
			return;
		} else if (value === this.props.policy.website) {
			// catches edge case where the user changes an existing
			// website rule, and selects the same website, causes website to
			// be set as the signature.
			this.setState({
				signatureType: SIGNATURE,
				signature: undefined,
				website: value,
				signatureValueInvalid: false,
				signatureError: "",
				modifiedWebsite: false,
			});
			return;
		}

		const signature = useSignatureStore.getState().findSignature(value);
		if (signature) {
			this.setState({
				signatureType: SIGNATURE,
				signature: value,
				website: undefined,
				signatureValueInvalid: false,
				signatureError: "",
				modifiedWebsite: true,
				isBrowser: signature.isBrowser,
			});
		} else {
			const appControl = this.props.nativeAppControlsDb.find(value);
			// it's a native app control, not a regular signature
			this.setState({
				signatureType: APP_CONTROL,
				signature: value,
				isBrowser: appControl?.isBrowser,
			});
		}
	};

	changeUser = (value) => {
		this.setState({
			user: value,
			userValueInvalid: false,
			userError: "",
		});
	};

	changeType = (value) => {
		this.setState({
			type: value,
			actionValueInvalid: value === undefined,
		});
	};

	changeWholeClass = (value) => {
		this.setState({
			wholeClass: value,
			user: undefined,
		});
	};

	validate = () => {
		let succeeded = true;
		if (!_.isEmpty(this.props.nativeAppControl)) {
			return succeeded;
		}

		if (this.state.signature === undefined && this.state.website === undefined) {
			succeeded = false;
			this.setState({
				signatureValueInvalid: true,
				signatureError: this.props.t("createPolicy.errorSelectSomethingToControl"),
			});
		} else {
			this.setState({
				signatureValueInvalid: false,
				signatureError: "",
			});
		}

		if (!this.state.wholeClass && (this.state.user === undefined || this.state.user === "")) {
			succeeded = false;
			this.setState({
				userValueInvalid: true,
				userError: this.props.t("createPolicy.errorSelectStudentOrClass"),
			});
		} else {
			this.setState({
				userValueInvalid: false,
				userError: "",
			});
		}

		return succeeded;
	};

	getRuleFromClassroomPolicyStore = (ruleId) => {
		const cpsState = useClassroomPolicyStore.getState();
		let filteredRule = cpsState.blocked.filter((rule) => rule.id === ruleId)[0];
		if (filteredRule) {
			const filteredRuleCopy = JSON.parse(JSON.stringify(filteredRule));
			filteredRuleCopy["type"] = "blocked";
			return filteredRuleCopy;
		}
		filteredRule = cpsState.allowed.filter((rule) => rule.id === ruleId)[0];
		if (filteredRule) {
			const filteredRuleCopy = JSON.parse(JSON.stringify(filteredRule));
			filteredRuleCopy["type"] = "allowed";
			return filteredRuleCopy;
		}
		return {};
	};

	handleValidationResponse = (data = {}, originalRule) => {
		// API contract (classroom-api): 409 body has lowercase "error" array, each item has "code", "id", "tag", "website"
		const errorList = Array.isArray(data?.error) ? data.error : data?.error != null ? [data.error] : [];
		const firstError = errorList[0];

		if (firstError) {
			let code = (firstError.code ?? "").toString();
			// 409 from backend often means locked-rule; if no code, treat as FILTER_RULE_VALIDATION_FAILED
			if (!code) {
				code = "FILTER_RULE_VALIDATION_FAILED";
			}
			const newState = {
				saving: false,
				validationErrorCode: code,
				validatedPolicy: {
					tag: firstError.tag,
					website: firstError.website,
				},
			};

			// For CW007 we don't revert. For FILTER_RULE_VALIDATION_FAILED (409 locked rule) preserve user's current blocked/allowed selection.
			if (
				this.props.editing &&
				!["CW007"].includes(newState.validationErrorCode) &&
				newState.validationErrorCode.toUpperCase() !== "FILTER_RULE_VALIDATION_FAILED"
			) {
				if (!originalRule) {
					originalRule = this.getRuleFromClassroomPolicyStore(this.state.id);
				}

				newState["type"] = originalRule.type;
				newState["originalType"] = originalRule.type ? originalRule.type : "blocked";
				newState["wholeClass"] = originalRule.user ? false : true;
				newState["editedlUser"] = this.state.user;
				newState["user"] = originalRule.user;
			}

			if (newState.validationErrorCode.includes("CW")) {
				const id = firstError.id;
				newState["duplicateOrConflictRule"] = this.getRuleFromClassroomPolicyStore(id?.[0]);
			} else if (newState.validationErrorCode.toUpperCase() === "FILTER_RULE_VALIDATION_FAILED") {
				const id = firstError.id;
				newState["duplicateOrConflictRule"] = {
					id: id?.[0] ?? "",
					tag: firstError.tag ?? this.state.signature,
					website: firstError.website ?? this.state.website,
					type: this.state.type,
					user: this.state.wholeClass ? undefined : this.state.user,
				};
			} else {
				console.log("Unhandled type of validation error");
			}

			this.setState(newState);
		} else {
			this.close();
		}
	};

	handleAppControlsValidationResponse = (resp) => {
		if (resp.error) {
			this.setState({ appControlValidationError: true, saving: false });
		} else {
			this.close();
		}
	};

	handleOnApiError = () => {
		const newState = { saving: false, networkError: true };
		this.setState(newState);
	};

	submit = (event = {}) => {
		if (!this.validate()) return;
		this.setState({ saving: true });

		if (this.state.signatureType === SIGNATURE) {
			if (this.state.type === "blocked") {
				useClassroomPolicyStore.getState().addBlockedRule(
					{
						tag: this.state.signature,
						website: this.state.website,
						permanent: true,
						enabled: true,
						user: this.state.wholeClass ? undefined : this.state.user,
					},
					{
						onApiError: this.handleOnApiError,
						callback: this.handleValidationResponse,
						skipValidations: event.skipValidations,
					}
				);
			} else {
				useClassroomPolicyStore.getState().addAllowedRule(
					{
						tag: this.state.signature,
						website: this.state.website,
						permanent: true,
						enabled: true,
						user: this.state.wholeClass ? undefined : this.state.user,
					},
					{
						onApiError: this.handleOnApiError,
						callback: this.handleValidationResponse,
						skipValidations: event.skipValidations,
					}
				);
			}
		} else {
			// native app control signature type
			const appControl = this.props.nativeAppControlsDb.find(this.state.signature);
			useClassroomPolicyStore.getState().addAppControl(
				{
					classroomStableIdV1: useSelectedClassroomStore.getState().stableIdV1,
					classroomId: useSelectedClassroomStore.getState().id,
					classroomSourceName: useSelectedClassroomStore.getState().sourceId,
					data: {
						name: appControl.name,
						isClassroomRule: this.state.wholeClass,
						studentStableIdV1: this.state.wholeClass
							? null
							: useSelectedClassroomStore.getState().students?.find((student) => student.username === this.state.user).stableIdV1,
						appControlId: appControl.id,
						action: this.state.type === "blocked" ? "block" : "allow",
					},
				},
				{ onApiError: this.handleOnApiError, callback: this.handleAppControlsValidationResponse }
			);
		}
	};

	modify = (event = {}) => {
		if (!this.validate()) return;
		this.setState({ saving: true });

		if (_.isEmpty(this.props.nativeAppControl)) {
			// originalType = which list the rule lives in; type = what user selected. Use originalType to pick the right store method.
			if (this.state.originalType === "allowed") {
				useClassroomPolicyStore.getState().modifyAllowedRule(
					{
						id: this.state.id,
						tag: this.state.signature,
						website: this.state.website,
						permanent: true,
						enabled: this.state.enabled,
						user: this.state.wholeClass ? undefined : this.state.user,
						type: this.state.type,
					},
					{
						onApiError: this.handleOnApiError,
						callback: this.handleValidationResponse,
						skipValidations: event.skipValidations,
					}
				);
			} else {
				useClassroomPolicyStore.getState().modifyBlockedRule(
					{
						id: this.state.id,
						tag: this.state.signature,
						website: this.state.website,
						permanent: true,
						enabled: this.state.enabled,
						user: this.state.wholeClass ? undefined : this.state.user,
						type: this.state.type,
					},
					{
						onApiError: this.handleOnApiError,
						callback: this.handleValidationResponse,
						skipValidations: event.skipValidations,
					}
				);
			}
		} else {
			// native app control
			useClassroomPolicyStore.getState().modifyAppControl(
				{
					classroomStableIdV1: useSelectedClassroomStore.getState().stableIdV1,
					classroomId: useSelectedClassroomStore.getState().id,
					classroomSourceName: useSelectedClassroomStore.getState().sourceId,
					data: {
						id: this.props.nativeAppControl.id,
						name: this.props.nativeAppControl.name,
						isClassroomRule: this.state.wholeClass,
						studentStableIdV1: this.state.wholeClass
							? null
							: useSelectedClassroomStore.getState().students?.find((student) => student.username === this.state.user).stableIdV1,
						appControlId: this.state.signature,
						action: this.state.type === "blocked" ? "block" : "allow",
					},
				},
				{ callback: this.handleAppControlsValidationResponse }
			);
		}
	};

	delete = () => {
		this.setState({ saving: true });
		if (_.isEmpty(this.props.nativeAppControl)) {
			if (this.state.originalType === "blocked") {
				useClassroomPolicyStore.getState().removeBlockedRule(
					{
						id: this.state.id,
						tag: this.state.signature,
						website: this.state.website,
						permanent: true,
						enabled: this.state.enabled,
						user: this.state.originalUser,
					},
					{ onApiError: this.handleOnApiError, callback: this.handleValidationResponse }
				);
			} else {
				useClassroomPolicyStore.getState().removeAllowedRule(
					{
						id: this.state.id,
						tag: this.state.signature,
						website: this.state.website,
						permanent: true,
						enabled: this.state.enabled,
						user: this.state.originalUser,
					},
					{ onApiError: this.handleOnApiError, callback: this.handleValidationResponse }
				);
			}
		} else {
			// native app control
			useClassroomPolicyStore.getState().removeAppControl(
				{
					id: this.props.nativeAppControl.id,
					classroomStableIdV1: useSelectedClassroomStore.getState().stableIdV1,
					classroomId: useSelectedClassroomStore.getState().id,
					classroomSourceName: useSelectedClassroomStore.getState().sourceId,
				},
				{
					callback: this.handleValidationResponse,
				}
			);
		}
	};

	resolve = () => {
		this.setState({
			duplicateOrConflictRule: undefined,
			resolving: true,
			validationErrorCode: undefined,
			validatedPolicy: undefined,
			id: this.state.duplicateOrConflictRule.id,
			type: this.state.duplicateOrConflictRule.type,
			originalType: this.state.duplicateOrConflictRule.type,
			wholeClass: this.state.duplicateOrConflictRule.user ? false : true,
			user: this.state.duplicateOrConflictRule.user,
			originalUser: this.state.duplicateOrConflictRule.user,
			signature: this.state.duplicateOrConflictRule.tag,
			website: this.state.duplicateOrConflictRule.website,
			enabled: this.state.duplicateOrConflictRule.enabled ?? true,
		});
	};

	resolveWithValidationsOff = () => {
		if (this.props.editing) {
			this.modify({ skipValidations: true });
		} else {
			this.submit({ skipValidations: true });
		}
	};

	close = () => {
		this.setState({
			id: undefined,
			signature: undefined,
			signatureType: undefined,
			website: undefined,
			user: undefined,
			originalUser: undefined,
			editedlUser: undefined,
			type: "blocked",
			originalType: "blocked",
			signatureValueInvalid: false,
			userValueInvalid: false,
			wholeClass: true,
			saving: false,
			duplicateOrConflictRule: undefined,
			resolving: false,
			validationErrorCode: undefined,
			validatedPolicy: undefined,
			isBrowser: false,
			appControlValidationError: false,
			networkError: false,
		});

		this.props.onClose();
	};

	renderButtons = () => {
		// When 409 response is in props, show Continue immediately (don't wait for state). API uses lowercase error/code.
		const hasLockedRuleFromProp =
			Array.isArray(this.props.validationResponseData?.error) &&
			this.props.validationResponseData.error[0]?.code === "FILTER_RULE_VALIDATION_FAILED";
		if (hasLockedRuleFromProp) {
			return (
				<div>
					<PrimaryButton
						className="mui-flat-button"
						label={this.props.t("createPolicy.continue")}
						onClick={() => (this.props.editing ? this.modify({ skipValidations: true }) : this.submit({ skipValidations: true }))}
					/>
				</div>
			);
		}

		if (this.state.saving) {
			return <PrimaryButton className="mui-flat-button" label={<Spinner color="white" width={"12px"} height={"12px"} />} />;
		}

		if (this.state.networkError) {
			return <GreyedButton className="mui-flat-button" label={this.props.t("policyDrawer.tryAgain")} onClick={this.close} />;
		}

		const isFilterRuleValidationFailed =
			(this.state.validationErrorCode ?? "").toString().toUpperCase() === "FILTER_RULE_VALIDATION_FAILED";
		if (isFilterRuleValidationFailed) {
			return (
				<div>
					<PrimaryButton
						className="mui-flat-button"
						label={this.props.t("createPolicy.continue")}
						onClick={() => (this.props.editing ? this.modify({ skipValidations: true }) : this.submit({ skipValidations: true }))}
					/>
				</div>
			);
		}

		if (this.state.duplicateOrConflictRule) {
			if (this.props.editing && ["CW001", "CW002", "CW003", "CW004", "CW005"].includes(this.state.validationErrorCode)) {
				return (
					<div>
						<DangerButton className="mui-flat-button" label={this.props.t("createPolicy.deleteRule")} onClick={this.delete} />
					</div>
				);
			} else if ((this.state.validationErrorCode?.toUpperCase?.() ?? this.state.validationErrorCode) === "FILTER_RULE_VALIDATION_FAILED") {
				return (
					<div>
						<PrimaryButton
							className="mui-flat-button"
							label={this.props.t("createPolicy.continue")}
							onClick={() => (this.props.editing ? this.modify({ skipValidations: true }) : this.submit({ skipValidations: true }))}
						/>
					</div>
				);
			} else if (["CW006"].includes(this.state.validationErrorCode)) {
				return (
					<div>
						<GreyedButton className="mui-flat-button" label={this.props.t("createPolicy.addRule")} />
					</div>
				);
			} else if (["CW007"].includes(this.state.validationErrorCode)) {
				return (
					<div>
						<PrimaryButton
							className="mui-flat-button"
							label={this.props.t("createPolicy.yesProceed")}
							onClick={this.resolveWithValidationsOff}
						/>
					</div>
				);
			} else {
				return (
					<div>
						<PrimaryButton className="mui-flat-button" label={this.props.t("createPolicy.yesEdit")} onClick={this.resolve} />
					</div>
				);
			}
		}

		if (this.props.editing || this.state.resolving) {
			return (
				<div>
					<DangerButton className="mui-flat-button" label={this.props.t("createPolicy.deleteRule")} onClick={this.delete} />
					<PrimaryButton className="mui-flat-button" label={this.props.t("createPolicy.update")} onClick={this.modify} />
				</div>
			);
		}

		return (
			<div>
				<PrimaryButton className="mui-flat-button" label={this.props.t("createPolicy.addRule")} onClick={this.submit} />
			</div>
		);
	};

	openZenDeskHowClasswizeRulesWork = () => {
		window.open("https://help.classwize.net/hc/en-gb/articles/5590349042204-How-Classwize-Rules-Work", "_blank");
	};

	getDisplayName = (username) => {
		if (!username) {
			return this.props.t("createPolicy.wholeClassDisplay");
		}
		const user = this.props.users.filter((user) => user.username === username)[0];
		return userDisplayText(user);
	};

	renderServerValidationWarnings = () => {
		const code = (this.state.validationErrorCode ?? "").toString();
		// Show locked-rule warning from state OR directly from 409 response in props (create or edit).
		const fromProp =
			Array.isArray(this.props.validationResponseData?.error) &&
			this.props.validationResponseData.error[0]?.code === "FILTER_RULE_VALIDATION_FAILED";
		const isFilterRuleValidationFailed = code === "FILTER_RULE_VALIDATION_FAILED" || fromProp;

		// Show locked-rule warning when we have FILTER_RULE_VALIDATION_FAILED even if duplicateOrConflictRule is missing
		if (isFilterRuleValidationFailed) {
			return (
				<div className="CreatePolicy_lockedRuleBlock">
					<br />
					<WarningBanner
						title={this.props.t("createPolicy.lockedRuleBannerTitle")}
						message={this.props.t("createPolicy.lockedRuleBannerMessage")}
					/>
				</div>
			);
		}

		if (!this.state.duplicateOrConflictRule) {
			return;
		}

		if (code.includes("CW")) {
			if (this.props.editing) {
				if (["CW002"].includes(code)) {
					const isBlocked = this.state.duplicateOrConflictRule.type === "blocked";
					const app = this.renderApp(this.state.duplicateOrConflictRule);
					const userDisplay = this.getDisplayName(this.state.originalUser);
					const i18nKey = isBlocked ? "createPolicy.cw002EditingBlocked" : "createPolicy.cw002EditingAllowed";
					return (
						<ul className="CreatePolicy_Warning">
							<li>
								<ChakraFAIcon icon="fa-exclamation-triangle" variant="solid" className="CreatePolicy_WarningIcon" />
							</li>
							<li>
								<Trans i18nKey={i18nKey} values={{ app, userDisplay }} components={{ 1: <b /> }} />
							</li>
						</ul>
					);
				} else if (["CW001", "CW004"].includes(code)) {
					const isBlocked = this.state.duplicateOrConflictRule.type === "blocked";
					const app = this.renderApp(this.state.duplicateOrConflictRule);
					const userDisplay = this.getDisplayName(this.state.originalUser);
					const i18nKey = isBlocked ? "createPolicy.cw001EditingBlocked" : "createPolicy.cw001EditingAllowed";
					return (
						<ul className="CreatePolicy_Warning">
							<li>
								<ChakraFAIcon icon="fa-exclamation-triangle" variant="solid" className="CreatePolicy_WarningIcon" />
							</li>
							<li>
								<Trans i18nKey={i18nKey} values={{ app, userDisplay }} components={{ 1: <b /> }} />
							</li>
						</ul>
					);
				} else if (["CW003", "CW005"].includes(code)) {
					const isBlocked = this.state.duplicateOrConflictRule.type === "blocked";
					const app = this.renderApp(this.state.duplicateOrConflictRule);
					const userEdited = this.getDisplayName(this.state.editedlUser);
					const userOriginal = this.getDisplayName(this.state.originalUser);
					const i18nKey = isBlocked ? "createPolicy.cw003EditingBlocked" : "createPolicy.cw003EditingAllowed";
					return (
						<ul className="CreatePolicy_Warning">
							<li>
								<ChakraFAIcon icon="fa-exclamation-triangle" variant="solid" className="CreatePolicy_WarningIcon" />
							</li>
							<li>
								<Trans i18nKey={i18nKey} values={{ userEdited, userOriginal, app }} components={{ 1: <b /> }} />
							</li>
						</ul>
					);
				} else if (["CW006"].includes(code)) {
					const app = this.renderApp(this.state.duplicateOrConflictRule);
					const validatedApp = this.renderApp(this.state.validatedPolicy);
					return (
						<ul className="CreatePolicy_Warning">
							<li>
								<ChakraFAIcon icon="fa-exclamation-triangle" variant="solid" className="CreatePolicy_WarningIcon" />
							</li>
							<li>
								<Trans i18nKey="createPolicy.cw006Conflict" values={{ app, validatedApp }} components={{ 1: <b /> }} />
							</li>
						</ul>
					);
				} else if (["CW007"].includes(code)) {
					const app = this.renderApp(this.state.duplicateOrConflictRule);
					return (
						<ul className="CreatePolicy_Warning">
							<li>
								<ChakraFAIcon icon="fa-exclamation-triangle" variant="solid" className="CreatePolicy_WarningIcon" />
							</li>
							<li>
								<Trans i18nKey="createPolicy.cw007EditingProceed" values={{ app }} components={{ 1: <b /> }} />
							</li>
						</ul>
					);
				}
			} else {
				if (["CW006"].includes(code)) {
					const app = this.renderApp(this.state.duplicateOrConflictRule);
					const validatedApp = this.renderApp(this.state.validatedPolicy);
					return (
						<ul className="CreatePolicy_Warning">
							<li>
								<ChakraFAIcon icon="fa-exclamation-triangle" variant="solid" className="CreatePolicy_WarningIcon" />
							</li>
							<li>
								<Trans i18nKey="createPolicy.cw006Conflict" values={{ app, validatedApp }} components={{ 1: <b /> }} />
							</li>
						</ul>
					);
				} else if (["CW007"].includes(code)) {
					const app = this.renderApp(this.state.duplicateOrConflictRule);
					return (
						<ul className="CreatePolicy_Warning">
							<li>
								<ChakraFAIcon icon="fa-exclamation-triangle" variant="solid" className="CreatePolicy_WarningIcon" />
							</li>
							<li>
								<Trans i18nKey="createPolicy.cw007CreatingProceed" values={{ app }} components={{ 1: <b /> }} />
							</li>
						</ul>
					);
				} else {
					const app = this.renderApp(this.state.duplicateOrConflictRule);
					return (
						<ul className="CreatePolicy_Warning">
							<li>
								<ChakraFAIcon icon="fa-exclamation-triangle" variant="solid" className="CreatePolicy_WarningIcon" />
							</li>
							<li>
								<Trans i18nKey="createPolicy.duplicateRuleEditPrompt" values={{ app }} components={{ 1: <b /> }} />
							</li>
						</ul>
					);
				}
			}
		}
	};

	renderAppControlsWarnings = () => {
		if (this.state.appControlValidationError) {
			const appControl = this.props.nativeAppControlsDb.find(this.state.signature);
			setTimeout(() => {
				this.formRef.current?.scrollIntoView({ block: "end" });
			}, 16);
			const mutation = this.props.t(this.props.editing ? "createPolicy.mutationUpdate" : "createPolicy.mutationCreate");
			return (
				<ul className="CreatePolicy_Warning">
					<li>
						<ChakraFAIcon icon="fa-exclamation-triangle" variant="solid" className="CreatePolicy_WarningIcon" />
					</li>
					<li>{this.props.t("createPolicy.appControlConflict", { mutation, appName: appControl.name })}</li>
				</ul>
			);
		}
	};

	renderApp = (policy) => {
		if (policy.website) {
			return policy.website;
		}

		if (policy.tag) {
			return useSignatureStore.getState().lookupName(policy.tag);
		}

		return this.props.t("policyDrawer.allApps");
	};

	renderWebsiteIcon = (policy) => {
		if (policy.tag) {
			const faviconUrl = useSignatureStore.getState().getFavicon(policy.tag);

			if (faviconUrl && faviconUrl !== "") {
				return (
					<div>
						<SafeImage styleClassName="CreatePolicy_ruleFavicon" src={faviconUrl} />
					</div>
				);
			}
		} else if (policy.website) {
			return <SafeImage styleClassName="CreatePolicy_ruleFavicon" src="/static/images/Icon_URL.svg" />;
		}

		return <SafeImage styleClassName="CreatePolicy_ruleFavicon" src="/static/images/Icon_Folder.svg" />;
	};

	renderWebsite = (policy) => {
		if (this.state.resolving || this.props.editing) {
			if (_.isEmpty(this.props.nativeAppControl)) {
				return (
					<div>
						{this.renderWebsiteIcon(policy)}
						{this.renderApp(policy)}
					</div>
				);
			} else {
				// native app control
				return (
					<div>
						{/* icon */}
						{!_.isEmpty(this.props.nativeAppControl.favicon) ? (
							<SafeImage styleClassName="CreatePolicy_ruleFavicon" src={this.props.nativeAppControl.favicon} />
						) : (
							<SafeImage styleClassName="CreatePolicy_ruleFavicon" src="/static/images/Icon_Folder.svg" />
						)}
						{/* name */}
						{this.props.nativeAppControl.name}
					</div>
				);
			}
		} else {
			return (
				<div>
					<label className="block-label--bold">{this.props.t("createPolicy.labelThisRuleControls")}</label>
					<NuSingleSelect
						tabIndex={0}
						allItems={this.prepareSelectorItems()}
						selectedItem={this.getSelected()}
						searchPlaceholder={this.props.t("createPolicy.searchPlaceholder")}
						onChange={this.changeSignature}
						invalid={this.state.signatureValueInvalid}
					/>
					{this.state.signatureValueInvalid && <div className="error">{this.state.signatureError}</div>}
					{this.addWarningsToBlockBrowsers()}
				</div>
			);
		}
	};

	addWarningsToBlockBrowsers = () => {
		return (
			this.state.isBrowser && (
				<ul className="CreatePolicy_Warning">
					<li>
						<div className="CreatePolicy_Warning_Title">
							<ChakraFAIcon icon="fa-exclamation-triangle" variant="solid" className="CreatePolicy_WarningIcon" />
							{this.props.t("createPolicy.browserWarningTitle")}
						</div>
						<div className="CreatePolicy_Warning_Body">{this.props.t("createPolicy.browserWarningBody")}</div>
					</li>
				</ul>
			)
		);
	};

	getSelected = () => {
		if (this.state.website) {
			return !this.state.modifiedWebsite ? this.state.website : "website";
		}
		return this.state.signature;
	};

	render() {
		const modalTitle =
			(this.props.editing || this.state.resolving) !== true
				? this.props.t("createPolicy.modalTitleAdd")
				: this.props.t("createPolicy.modalTitleEdit");

		const policy = {
			id: this.state.id,
			tag: this.state.signature,
			website: this.state.website,
			permanent: true,
			user: this.state.user,
		};

		return (
			<div>
				<StandardModal title={modalTitle} open={this.props.open} actions={this.renderButtons()} close={this.close}>
					<form onSubmit={this.submit} ref={this.formRef}>
						<div>
							<div style={{ lineHeight: "1.5" }}>
								{this.props.t("createPolicy.introControlAccess")}
								{this.featureFlags.isNativeAppControlsEnabled() ? (
									<>
										<br />
										<Trans
											i18nKey="createPolicy.windowsAppListHint"
											components={{
												appListLink: (
													<a
														className="link"
														href="https://help.classwize.net/hc/en-gb/articles/8675151325212"
														target="_blank"
														rel="noreferrer"
													/>
												),
											}}
										/>
									</>
								) : null}
							</div>
							<br />
							{this.renderWebsite(policy)}
							<br />
							<div>
								<label htmlFor="create_rule_status_toggle" className="block-label--bold">
									{this.props.t("createPolicy.duringClassLabel")}
								</label>
								{this.state.classwizeConfig.create_blocking_rules_only ? (
									<RuleStatusPill type="blocked" />
								) : (
									<RuleStatusToggle id="create_rule_status_toggle" onChange={this.changeType} type={this.state.type} />
								)}
							</div>
							<br />
							<div className="CreatePolicy_checkboxRow">
								<div className="CreatePolicy_checkboxRowLabel">
									<label className="text-bold">{this.props.t("createPolicy.appliesToLabel")}</label>
								</div>
								<div className="CreatePolicy_checkboxRowCheckbox">
									<Checkbox
										checked={this.state.wholeClass}
										onChecked={(isChecked) => this.setState({ wholeClass: isChecked })}
										label={this.props.t("createPolicy.wholeClass")}
									/>
								</div>
							</div>
							<div>
								<DisableKeyboardNavigation enabled={this.state.wholeClass} className="disabled-wrapper">
									<NuSingleSelect
										allItems={this.prepareAvailableUsers()}
										selectedItem={this.state.user}
										searchPlaceholder={this.props.t("createPolicy.studentSearchPlaceholder")}
										onChange={this.changeUser}
										invalid={!this.state.wholeClass && this.state.userValueInvalid}
									/>
									{!this.state.wholeClass && this.state.userValueInvalid && <div className="error">{this.state.userError}</div>}
								</DisableKeyboardNavigation>
							</div>
							{this.state.signatureType === APP_CONTROL ? this.renderAppControlsWarnings() : this.renderServerValidationWarnings()}
						</div>
					</form>
				</StandardModal>
			</div>
		);
	}
}

CreatePolicy.propTypes = {
	nativeAppControlsDb: PropTypes.object,
	policy: PropTypes.object,
	onClose: PropTypes.func,
	open: PropTypes.bool,
	editing: PropTypes.bool,
	validationResponseData: PropTypes.object,
	users: PropTypes.array,
	t: PropTypes.func.isRequired,
};

export default withTranslation()(CreatePolicy);
